import { getFromDataPath } from "../../utility";
import * as ARS from "../ars_ldm";
import { useArsContext } from "../contexts";
import { ArsSelect, ArsText, ArsVariable } from "../inputs";
import { isAnalysisMethod, isOperation } from "../typeGuards";
import { isGroupingFactor } from "../typeGuards/isGroupingFactor";
import { useArsTable } from "./ArsTableContextProvider";
import { ArsTableItemParameterValue } from "./ArsTableItemParameterValue";
import { ArsTextJustification } from "./interface";
import { isArsTableGroupCalculationDto } from "./typeguards";

export interface ArsTableGroupingFactorParametersProps {
  dataPath: string;
  disabled?: boolean;
}

/**
 * Editable parameters for a grouping factor in the ArsTable.
 *
 * @param props - The props for the component
 * @param props.dataPath - The path to the grouping factor in the table context data structure.
 * @param props.disabled - Optional boolean to indicate if the component should be disabled.
 */
export const ArsTableItemParameters = <T,>({
  dataPath,
  disabled,
}: ArsTableGroupingFactorParametersProps): React.ReactNode => {
  const ars = useArsContext<T>();
  const { state } = useArsTable<T>();
  const tableBodyData = state?.bodyData;

  const thisItem = tableBodyData
    ? getFromDataPath(dataPath, tableBodyData, (obj) =>
        isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
      )
    : null;

  const analysisMethod =
    state && state.bodyData.analysisMethodId
      ? getFromDataPath(
          `/analysisMethods/${state.bodyData.analysisMethodId}`,
          ars.state,
          isAnalysisMethod,
        )
      : null;
  if (!state || !thisItem || !analysisMethod) return null;

  const groupingFactor = tableBodyData
    ? getFromDataPath(`${dataPath}/groupingFactor`, tableBodyData, isGroupingFactor)
    : // istanbul ignore next
      null;
  const operation = tableBodyData
    ? getFromDataPath(`${dataPath}/operation`, tableBodyData, isOperation)
    : // istanbul ignore next
      null;

  const availableParameters: ARS.TemplateCodeParameter[] =
    analysisMethod.codeTemplate?.parameters?.filter(
      (tcp) =>
        (tcp.valueSource === "groupingFactor" && groupingFactor) ||
        (tcp.valueSource === "operation" && operation),
    ) ?? [];

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {operation && (
        <ArsVariable
          id={`${dataPath.replace(/\//g, "-")}-target-variable`}
          combinedDataPath={`${dataPath}/targetVariable`}
          tableDataPath
          label="Target variable"
          labelWidth={5}
          disabled={disabled}
        />
      )}
      {availableParameters.map((parameter, index) => (
        <ArsTableItemParameterValue
          key={`${parameter.name}-${index}`}
          parameter={parameter}
          index={index}
          dataPath={dataPath}
          disabled={disabled}
        />
      ))}
      {operation && (
        <>
          <ArsSelect
            id={`${dataPath.replace(/\//g, "-")}-justification`}
            dataPath={`${dataPath}/justification`}
            tableDataPath
            label="Justification"
            labelWidth={5}
            options={Object.entries(ArsTextJustification).map(([label, value]) => ({
              value,
              label,
            }))}
            disabled={disabled}
          />
          <ArsText
            id={`${dataPath.replace(/\//g, "-")}-decimal-align-percent`}
            dataPath={`${dataPath}/decimalAlignPercent`}
            disabled={disabled}
            label="Decimal align %"
            labelWidth={5}
            type="number"
            tableDataPath
          />
        </>
      )}
    </div>
  );
};

ArsTableItemParameters.displayName = "ArsTableItemParameters";
